import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry';

type DocumentRequestRow = {
  id: string;
  supplier_id: string;
  organisation_id: string;
  due_date: string | null;
  message: string | null;
  status: 'pending' | 'completed' | 'expired';
  deleted_at: string | null;
  completed_at: string | null;
  created_at: string | null;
};

type UploadInvocationResult = {
  success: boolean;
  bucket: string;
  path: string;
  token: string;
  signed_url: string | null;
  file_name: string;
  content_type: string | null;
  request: {
    id: string;
    supplier_id: string;
    organisation_id: string;
    due_date: string | null;
    status: string;
  };
};

type DocumentRequestItemRow = {
  id: string;
  request_id: string;
  requested_document_name: string;
  storage_path: string | null;
  uploaded_at: string | null;
  status: 'pending' | 'uploaded';
  supplier_document_id?: string | null;
  valid_until?: string | null;
  signed_url?: string | null;
};

const pageStyles = {
  shell: {
    minHeight: '100vh',
    background: '#fafaf9',
  },
  card: {
    border: '1px solid #e7e5e4',
    borderRadius: '16px',
    background: '#ffffff',
  },
} satisfies Record<string, React.CSSProperties>;

const isExpired = (dueDate: string | null) => {
  if (!dueDate) {
    return false;
  }

  const dueDateValue = new Date(`${dueDate}T23:59:59.999Z`);
  if (Number.isNaN(dueDateValue.getTime())) {
    return false;
  }

  return Date.now() > dueDateValue.getTime();
};

export default function SupplyChainDocumentUpload() {
  const { accessToken } = useParams<{ accessToken: string }>();
  const [request, setRequest] = useState<DocumentRequestRow | null>(null);
  const [requestingCompanyName, setRequestingCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestItems, setRequestItems] = useState<DocumentRequestItemRow[]>(
    [],
  );
  const [requestItemsLoading, setRequestItemsLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<Record<string, File | null>>(
    {},
  );
  const [pendingValidUntil, setPendingValidUntil] = useState<
    Record<string, string>
  >({});
  const [fileInputResetKey, setFileInputResetKey] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittingItemId, setSubmittingItemId] = useState<string | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const bucketName = useMemo(() => 'supply-chain-compliance-documents', []);
  const uploadedRequestItems = useMemo(
    () => requestItems.filter((item) => item.status === 'uploaded'),
    [requestItems],
  );
  const requestedDocumentCount = Math.max(requestItems.length || 1, 1);
  const missingDocumentCount = Math.max(
    requestedDocumentCount - uploadedRequestItems.length,
    0,
  );

  const requestUnavailableReason = useMemo(() => {
    if (!request) {
      return null;
    }

    if (request.deleted_at) {
      return 'This document request is no longer available.';
    }

    if (request.status === 'completed' || request.completed_at) {
      return 'This document request has already been completed.';
    }

    if (request.status === 'expired' || isExpired(request.due_date)) {
      return 'This document request has expired.';
    }

    return null;
  }, [request]);

  const fetchRequestingCompanyName = useCallback(
    async (organisationId: string) => {
      const { data, error: fetchError } = await supabase
        .from('organisations')
        .select('name')
        .eq('id', organisationId)
        .maybeSingle();

      if (fetchError) {
        captureSupabaseError(fetchError, {
          op: 'supply-chain.document-request.public.organisation.fetch',
        });
        setRequestingCompanyName('The requesting company');
        return;
      }

      setRequestingCompanyName(data?.name ?? 'The requesting company');
    },
    [],
  );

  const fetchRequestItems = useCallback(
    async (documentRequest: DocumentRequestRow | null) => {
      if (!documentRequest) {
        setRequestItems([]);
        return;
      }

      setRequestItemsLoading(true);

      const { data, error: fetchError } = await supabase
        .from('supply_chain_document_request_items')
        .select(
          'id, request_id, requested_document_name, storage_path, uploaded_at, status',
        )
        .eq('request_id', documentRequest.id)
        .order('requested_document_name', { ascending: true });

      if (fetchError) {
        captureSupabaseError(fetchError, {
          op: 'supply-chain.document-request.public.items.fetch',
        });
        setSubmitError(fetchError.message);
        setRequestItems([]);
        setRequestItemsLoading(false);
        return;
      }

      const itemRows = (data ?? []) as DocumentRequestItemRow[];
      const storagePaths = itemRows
        .map((item) => item.storage_path)
        .filter((path): path is string => Boolean(path));
      let supplierDocumentsByPath = new Map<
        string,
        { id: string; valid_until: string | null }
      >();

      if (storagePaths.length > 0) {
        const { data: supplierDocuments, error: supplierDocumentsError } =
          await supabase
            .from('supply_chain_supplier_documents')
            .select('id, file_url, valid_until')
            .eq('supplier_id', documentRequest.supplier_id)
            .eq('organisation_id', documentRequest.organisation_id)
            .in('file_url', storagePaths);

        if (supplierDocumentsError) {
          captureSupabaseError(supplierDocumentsError, {
            op: 'supply-chain.document-request.public.supplier-documents.fetch',
          });
        } else {
          supplierDocumentsByPath = new Map(
            (
              (supplierDocuments ?? []) as Array<{
                id: string;
                file_url: string;
                valid_until: string | null;
              }>
            ).map((document) => [
              document.file_url,
              { id: document.id, valid_until: document.valid_until },
            ]),
          );
        }
      }

      const signedItems = await Promise.all(
        itemRows.map(async (item) => {
          const matchingSupplierDocument = item.storage_path
            ? supplierDocumentsByPath.get(item.storage_path)
            : undefined;

          if (!item.storage_path) {
            return {
              ...item,
              supplier_document_id: null,
              valid_until: null,
            };
          }

          if (item.storage_path.startsWith('http')) {
            return {
              ...item,
              supplier_document_id: matchingSupplierDocument?.id ?? null,
              valid_until: matchingSupplierDocument?.valid_until ?? null,
              signed_url: item.storage_path,
            };
          }

          const { data: signedUrlData, error: signedUrlError } =
            await supabase.storage
              .from(bucketName)
              .createSignedUrl(item.storage_path, 60 * 15);

          if (signedUrlError) {
            captureSupabaseError(signedUrlError, {
              op: 'supply-chain.document-request.public.items.signed-url',
            });
          }

          return {
            ...item,
            supplier_document_id: matchingSupplierDocument?.id ?? null,
            valid_until: matchingSupplierDocument?.valid_until ?? null,
            signed_url: signedUrlData?.signedUrl ?? null,
          };
        }),
      );

      setRequestItems(signedItems);
      setRequestItemsLoading(false);
    },
    [bucketName],
  );

  const fetchRequest = useCallback(async () => {
    if (!accessToken) {
      setError('Document request token is missing.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('supply_chain_document_requests')
      .select(
        'id, supplier_id, organisation_id, due_date, message, status, deleted_at, completed_at, created_at',
      )
      .eq('access_token', accessToken)
      .maybeSingle();

    if (fetchError) {
      captureSupabaseError(fetchError, {
        op: 'supply-chain.document-request.public.fetch',
      });
      setError(fetchError.message);
      setRequest(null);
      setLoading(false);
      return;
    }

    if (!data) {
      setError('Invalid document request token.');
      setRequest(null);
      setLoading(false);
      return;
    }

    const requestRow = data as DocumentRequestRow;

    setRequest(requestRow);
    void fetchRequestingCompanyName(requestRow.organisation_id);
    void fetchRequestItems(requestRow);
    setLoading(false);
  }, [accessToken, fetchRequestItems, fetchRequestingCompanyName]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const handleUpload = useCallback(
    async (requestItem: DocumentRequestItemRow) => {
      if (!accessToken) {
        setSubmitError('Document request token is missing.');
        return;
      }

      if (requestUnavailableReason) {
        setSubmitError(requestUnavailableReason);
        return;
      }

      const selectedFile = pendingFiles[requestItem.id] ?? null;
      const validUntil = pendingValidUntil[requestItem.id] ?? '';

      if (!selectedFile) {
        setSubmitError('Please select a file to upload.');
        return;
      }

      if (!validUntil) {
        setSubmitError('Please select a valid until date.');
        return;
      }

      setSubmittingItemId(requestItem.id);
      setSubmitError(null);
      setCompleteError(null);
      setSuccessMessage(null);

      try {
        const { data, error: invokeError } = await supabase.functions.invoke(
          'supply-chain-document-upload',
          {
            body: {
              accessToken,
              requestItemId: requestItem.id,
              fileName: selectedFile.name,
              contentType: selectedFile.type || null,
            },
          },
        );

        if (invokeError) {
          throw invokeError;
        }

        const parsed =
          typeof data === 'string'
            ? (JSON.parse(data) as UploadInvocationResult)
            : (data as UploadInvocationResult);

        const { error: uploadError } = await supabase.storage
          .from(parsed.bucket)
          .uploadToSignedUrl(parsed.path, parsed.token, selectedFile, {
            contentType: selectedFile.type || undefined,
          });

        if (uploadError) {
          throw uploadError;
        }

        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        const { error: supplierDocumentInsertError } = await supabase
          .from('supply_chain_supplier_documents')
          .insert({
            supplier_id: request?.supplier_id,
            organisation_id: request?.organisation_id,
            file_name: requestItem.requested_document_name,
            file_url: parsed.path,
            uploaded_by: user?.id ?? null,
            valid_until: validUntil,
          });

        if (supplierDocumentInsertError) {
          throw supplierDocumentInsertError;
        }

        const { error: itemUpdateError } = await supabase
          .from('supply_chain_document_request_items')
          .update({
            storage_path: parsed.path,
            uploaded_at: new Date().toISOString(),
            status: 'uploaded',
          })
          .eq('id', requestItem.id)
          .eq('request_id', requestItem.request_id);

        if (itemUpdateError) {
          throw itemUpdateError;
        }

        setPendingFiles((currentFiles) => ({
          ...currentFiles,
          [requestItem.id]: null,
        }));
        setPendingValidUntil((currentValues) => ({
          ...currentValues,
          [requestItem.id]: '',
        }));
        setFileInputResetKey((currentKey) => currentKey + 1);
        setSuccessMessage('Document uploaded successfully.');
        await fetchRequestItems(request);
      } catch (uploadError) {
        captureSupabaseError(uploadError, {
          op: 'supply-chain.document-request.public.upload',
        });
        setSubmitError(
          uploadError instanceof Error
            ? uploadError.message
            : 'Unable to upload document.',
        );
      } finally {
        setSubmittingItemId(null);
      }
    },
    [
      accessToken,
      fetchRequestItems,
      pendingFiles,
      pendingValidUntil,
      request,
      requestUnavailableReason,
    ],
  );

  const handleRemoveDocument = useCallback(
    async (requestItem: DocumentRequestItemRow) => {
      if (!confirm('Remove this uploaded file?')) {
        return;
      }

      setDeletingItemId(requestItem.id);
      setSubmitError(null);
      setSuccessMessage(null);

      try {
        if (
          requestItem.storage_path &&
          !requestItem.storage_path.startsWith('http')
        ) {
          const { error: removeStorageError } = await supabase.storage
            .from(bucketName)
            .remove([requestItem.storage_path]);

          if (removeStorageError) {
            throw removeStorageError;
          }
        }

        const { error: itemUpdateError } = await supabase
          .from('supply_chain_document_request_items')
          .update({
            storage_path: null,
            uploaded_at: null,
            status: 'pending',
          })
          .eq('id', requestItem.id)
          .eq('request_id', requestItem.request_id);

        if (itemUpdateError) {
          throw itemUpdateError;
        }

        if (requestItem.supplier_document_id) {
          const { error: supplierDocumentDeleteError } = await supabase
            .from('supply_chain_supplier_documents')
            .delete()
            .eq('id', requestItem.supplier_document_id);

          if (supplierDocumentDeleteError) {
            throw supplierDocumentDeleteError;
          }
        }

        setSuccessMessage('Uploaded file removed.');
        await fetchRequestItems(request);
      } catch (removeError) {
        captureSupabaseError(removeError, {
          op: 'supply-chain.document-request.public.remove',
        });
        setSubmitError(
          removeError instanceof Error
            ? removeError.message
            : 'Unable to remove uploaded file.',
        );
      } finally {
        setDeletingItemId(null);
      }
    },
    [bucketName, fetchRequestItems, request],
  );

  const handleCompleteRequest = useCallback(async () => {
    if (!request || !accessToken) {
      setCompleteError('Document request is unavailable.');
      return;
    }

    setCompleting(true);
    setCompleteError(null);
    setSubmitError(null);
    setSuccessMessage(null);

    try {
      const { error: updateError } = await supabase.functions.invoke(
        'supply-chain-document-request-complete',
        {
          body: {
            accessToken,
          },
        },
      );

      if (updateError) {
        throw updateError;
      }

      setShowCompleteModal(false);
      setSuccessMessage('Document request completed successfully.');
      await fetchRequest();
    } catch (completionError) {
      captureSupabaseError(completionError, {
        op: 'supply-chain.document-request.public.complete',
      });
      setCompleteError(
        completionError instanceof Error
          ? completionError.message
          : 'Unable to complete document request.',
      );
    } finally {
      setCompleting(false);
    }
  }, [accessToken, fetchRequest, request]);

  return (
    <div style={pageStyles.shell} className="py-5">
      <Container style={{ maxWidth: '720px' }}>
        <Card style={pageStyles.card}>
          <Card.Body className="p-4 p-lg-5">
            <div className="mb-4">
              <div
                className="text-uppercase fw-semibold mb-2"
                style={{
                  letterSpacing: '0.12em',
                  fontSize: '0.78rem',
                  color: '#78716c',
                }}
              >
                Supply Chain
              </div>
              <h1 className="h3 mb-2">Document Upload Request</h1>
              <p className="text-muted mb-0">
                Upload the requested compliance documents securely using the
                link you received.
              </p>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {error && (
                  <Alert variant="danger" className="mb-0">
                    {error}
                  </Alert>
                )}

                {!error && request ? (
                  <>
                    <div className="d-flex flex-column gap-1">
                      <div className="fw-semibold">
                        {requestingCompanyName || 'The requesting company'} has
                        requested {requestedDocumentCount}{' '}
                        {requestedDocumentCount === 1
                          ? 'document'
                          : 'documents'}
                        .
                      </div>
                      <div className="text-muted small">
                        Uploaded {uploadedRequestItems.length} of{' '}
                        {requestedDocumentCount} requested.
                      </div>
                    </div>

                    {request.message ? (
                      <Alert
                        variant="light"
                        className="mb-0"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {request.message}
                      </Alert>
                    ) : null}

                    {request.due_date ? (
                      <div className="text-muted small">
                        Due date:{' '}
                        {new Date(
                          `${request.due_date}T00:00:00`,
                        ).toLocaleDateString()}
                      </div>
                    ) : null}

                    {requestItemsLoading ? (
                      <div className="text-center py-2">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : null}

                    {requestUnavailableReason ? (
                      <Alert variant="warning" className="mb-0">
                        {requestUnavailableReason}
                      </Alert>
                    ) : (
                      <>
                        {submitError ? (
                          <Alert variant="danger" className="mb-0">
                            {submitError}
                          </Alert>
                        ) : null}

                        {successMessage ? (
                          <Alert variant="success" className="mb-0">
                            {successMessage}
                          </Alert>
                        ) : null}

                        {requestItems.length ? (
                          <div className="d-flex flex-column gap-3">
                            <div className="fw-semibold">
                              Requested documents
                            </div>
                            {requestItems.map((requestItem, index) => {
                              const selectedFile =
                                pendingFiles[requestItem.id] ?? null;
                              const isUploaded =
                                requestItem.status === 'uploaded';

                              return (
                                <div
                                  key={`file-input-${fileInputResetKey}-${requestItem.id}`}
                                  className="rounded border bg-white p-3"
                                >
                                  <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                                    <div>
                                      <div className="fw-semibold">
                                        {index + 1}.{' '}
                                        {requestItem.requested_document_name}
                                      </div>
                                      <div className="small text-muted">
                                        {isUploaded
                                          ? 'Uploaded'
                                          : 'Upload a file for this requested document.'}
                                      </div>
                                      {requestItem.valid_until ? (
                                        <div className="small text-muted">
                                          Valid until{' '}
                                          {new Date(
                                            `${requestItem.valid_until}T00:00:00`,
                                          ).toLocaleDateString()}
                                        </div>
                                      ) : null}
                                    </div>
                                    {isUploaded ? (
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() =>
                                          handleRemoveDocument(requestItem)
                                        }
                                        disabled={
                                          deletingItemId === requestItem.id
                                        }
                                      >
                                        {deletingItemId === requestItem.id
                                          ? 'Removing...'
                                          : 'Remove'}
                                      </Button>
                                    ) : null}
                                  </div>

                                  {isUploaded && requestItem.signed_url ? (
                                    <div className="small">
                                      <a
                                        href={requestItem.signed_url}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        View uploaded file
                                      </a>
                                    </div>
                                  ) : (
                                    <div className="d-flex flex-column gap-3">
                                      <div className="row g-3">
                                        <div className="col-md-8">
                                          <Form.Group className="mb-0">
                                            <Form.Label>File</Form.Label>
                                            <Form.Control
                                              type="file"
                                              accept=".pdf,.doc,.docx,.xlsx,.png,.jpg,.svg"
                                              onChange={(event) => {
                                                const input =
                                                  event.target as HTMLInputElement;
                                                const nextFile =
                                                  input.files?.[0] ?? null;
                                                setPendingFiles(
                                                  (currentFiles) => ({
                                                    ...currentFiles,
                                                    [requestItem.id]: nextFile,
                                                  }),
                                                );
                                              }}
                                              disabled={
                                                submittingItemId !== null
                                              }
                                            />
                                            {selectedFile ? (
                                              <div className="small text-muted mt-2">
                                                Selected: {selectedFile.name}
                                              </div>
                                            ) : null}
                                          </Form.Group>
                                        </div>
                                        <div className="col-md-4">
                                          <Form.Group className="mb-0">
                                            <Form.Label>Valid until</Form.Label>
                                            <Form.Control
                                              type="date"
                                              value={
                                                pendingValidUntil[
                                                  requestItem.id
                                                ] ?? ''
                                              }
                                              onChange={(event) =>
                                                setPendingValidUntil(
                                                  (currentValues) => ({
                                                    ...currentValues,
                                                    [requestItem.id]:
                                                      event.target.value,
                                                  }),
                                                )
                                              }
                                              disabled={
                                                submittingItemId !== null
                                              }
                                            />
                                          </Form.Group>
                                        </div>
                                      </div>

                                      <div className="d-grid">
                                        <Button
                                          onClick={() =>
                                            handleUpload(requestItem)
                                          }
                                          disabled={
                                            submittingItemId !== null ||
                                            !selectedFile ||
                                            !(
                                              pendingValidUntil[
                                                requestItem.id
                                              ] ?? ''
                                            )
                                          }
                                        >
                                          {submittingItemId === requestItem.id
                                            ? 'Uploading...'
                                            : 'Upload file'}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <Alert variant="warning" className="mb-0">
                            No requested documents were found for this request.
                          </Alert>
                        )}

                        <div className="d-grid">
                          <Button
                            variant="dark"
                            onClick={() => {
                              setCompleteError(null);
                              setShowCompleteModal(true);
                            }}
                            disabled={completing}
                          >
                            Complete request
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      <Modal
        show={showCompleteModal}
        onHide={() => {
          if (!completing) {
            setShowCompleteModal(false);
          }
        }}
        centered
      >
        <Modal.Header closeButton={!completing}>
          <Modal.Title>Complete request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column gap-3">
            <p className="mb-0">
              Confirm that you want to mark this document request as complete.
            </p>

            {missingDocumentCount > 0 ? (
              <Alert variant="warning" className="mb-0">
                You uploaded {uploadedRequestItems.length} of{' '}
                {requestedDocumentCount} requested{' '}
                {requestedDocumentCount === 1 ? 'document' : 'documents'}. If
                you continue, the request will still be marked as completed.
              </Alert>
            ) : (
              <Alert variant="success" className="mb-0">
                All requested files have been uploaded.
              </Alert>
            )}

            {completeError ? (
              <Alert variant="danger" className="mb-0">
                {completeError}
              </Alert>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowCompleteModal(false)}
            disabled={completing}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={handleCompleteRequest}
            disabled={completing}
          >
            {completing ? 'Completing...' : 'Confirm completion'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
